import { useRef, useEffect, useState, useCallback } from 'react'
import DrawToolbar from './DrawToolbar'

interface CapturaDrawCanvasProps {
  onConfirm: (blob: Blob) => void
  onCancel: () => void
}

interface Point {
  x: number
  y: number
}

const COLORS = ['#111111', '#ffffff', '#C4D44A', '#C8A84B', '#D93025']
const MAX_HISTORY = 20

export default function CapturaDrawCanvas({ onConfirm, onCancel }: CapturaDrawCanvasProps) {
  const canvasRef  = useRef<HTMLCanvasElement>(null)
  const drawing    = useRef(false)
  const lastPos    = useRef<Point | null>(null)
  const historyRef = useRef<ImageData[]>([])
  const [tool,      setTool]      = useState<'pen' | 'erase'>('pen')
  const [color,     setColor]     = useState('#111111')
  const [thickness, setThickness] = useState(4)
  const [canUndo,   setCanUndo]   = useState(false)

  // Init canvas branco — usa rAF para garantir layout completo
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    requestAnimationFrame(() => {
      const w = canvas.offsetWidth  || window.innerWidth
      const h = canvas.offsetHeight || (window.innerHeight - 120)
      canvas.width  = w
      canvas.height = h
      const ctx = canvas.getContext('2d')
      ctx.fillStyle = '#ffffff'
      ctx.fillRect(0, 0, w, h)
      historyRef.current = []
      setCanUndo(false)
    })
  }, [])

  function getPos(e: React.MouseEvent | React.TouchEvent, canvas: HTMLCanvasElement): Point {
    const rect = canvas.getBoundingClientRect()
    const src  = 'touches' in e && e.touches[0] ? e.touches[0] : (e as React.MouseEvent).nativeEvent
    return { x: src.clientX - rect.left, y: src.clientY - rect.top }
  }

  function saveHistory() {
    const canvas = canvasRef.current
    if (!canvas || !canvas.width || !canvas.height) return
    const ctx     = canvas.getContext('2d')
    const snapshot = ctx.getImageData(0, 0, canvas.width, canvas.height)
    historyRef.current = [...historyRef.current.slice(-MAX_HISTORY + 1), snapshot]
    setCanUndo(historyRef.current.length > 0)
  }

  function startDraw(e: React.MouseEvent | React.TouchEvent) {
    e.preventDefault()
    saveHistory()
    drawing.current = true
    lastPos.current = getPos(e, canvasRef.current)
  }

  function draw(e: React.MouseEvent | React.TouchEvent) {
    e.preventDefault()
    if (!drawing.current) return
    const canvas = canvasRef.current
    const ctx    = canvas.getContext('2d')
    const pos    = getPos(e, canvas)

    ctx.beginPath()
    ctx.moveTo(lastPos.current.x, lastPos.current.y)
    ctx.lineTo(pos.x, pos.y)
    ctx.strokeStyle = tool === 'erase' ? '#ffffff' : color
    ctx.lineWidth   = tool === 'erase' ? thickness * 4 : thickness
    ctx.lineCap     = 'round'
    ctx.lineJoin    = 'round'
    ctx.stroke()
    lastPos.current = pos
  }

  function endDraw() { drawing.current = false }

  const undo = useCallback(() => {
    if (!historyRef.current.length) return
    const canvas = canvasRef.current
    const ctx    = canvas.getContext('2d')
    const prev   = historyRef.current[historyRef.current.length - 1]
    ctx.putImageData(prev, 0, 0)
    historyRef.current = historyRef.current.slice(0, -1)
    setCanUndo(historyRef.current.length > 0)
  }, [])

  const clearCanvas = useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    saveHistory()
    const ctx = canvas.getContext('2d')
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, canvas.width, canvas.height)
  }, [])

  const handleConfirm = useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    // Se canvas sem dimensões (fallback: usa data URI)
    if (!canvas.width || !canvas.height) {
      onCancel()
      return
    }
    canvas.toBlob(blob => {
      if (blob) onConfirm(blob)
      else onCancel()
    }, 'image/png')
  }, [onConfirm, onCancel])

  return (
    <div className="fixed inset-0 z-[310] flex flex-col" style={{ background: '#f5f5f5' }}>
      <DrawToolbar
        tool={tool} setTool={setTool}
        color={color} setColor={setColor}
        thickness={thickness} setThickness={setThickness}
        colors={COLORS}
      />

      {/* Canvas */}
      <canvas
        ref={canvasRef}
        className="flex-1 w-full touch-none"
        onMouseDown={startDraw}  onMouseMove={draw}  onMouseUp={endDraw}  onMouseLeave={endDraw}
        onTouchStart={startDraw} onTouchMove={draw}  onTouchEnd={endDraw}
        style={{ cursor: tool === 'erase' ? 'cell' : 'crosshair' }}
      />

      {/* Barra de ações — linha 2 (bottom) */}
      <div className="flex-shrink-0 flex items-center gap-2 px-4 py-3 bg-[#1a1a1a] border-t border-white/[0.08]">

        <button
          onClick={undo}
          disabled={!canUndo}
          className="flex items-center gap-1 px-3 py-2 text-[12px] rounded-lg border-none bg-transparent cursor-pointer transition-colors disabled:opacity-30"
          style={{ color: canUndo ? '#ccc' : '#666' }}
        >
          <span className="material-symbols-outlined text-base">undo</span>
          Desfazer
        </button>
        <button
          onClick={clearCanvas}
          className="px-3 py-2 text-[12px] text-[#888] rounded-lg border-none bg-transparent cursor-pointer hover:text-[#f0f0f0]"
        >
          Limpar
        </button>

        <div className="flex-1" />

        <button
          onClick={onCancel}
          className="px-4 py-2 text-[13px] text-[#888] rounded-xl border-none bg-transparent cursor-pointer hover:text-[#f0f0f0]"
        >
          Cancelar
        </button>
        <button
          onClick={handleConfirm}
          className="px-5 py-2.5 text-[13px] font-semibold rounded-full border-none cursor-pointer"
          style={{ background: 'var(--color-hero)', color: '#111' }}
        >
          Usar
        </button>
      </div>
    </div>
  )
}
